import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import CmsService from "../services/CmsService";
import {ScreenHelper} from "../components/Utilities/ScreenHelper";
import {useTranslation} from "react-i18next";

const CmsContext = createContext();

export const CmsProvider = ({ children }) => {
    const [landingData, setLandingData] = useState({})
    const [pickProgramData, setPickProgramData] = useState([])
    const [questionnaireData, setQuestionnaireData] = useState([])
    const [productBannersData, setProductBannersData] = useState({})
    const [localeData, setLocaleData] = useState([])
    // const [appLoading   , setAppLoading] = useState(false);
    const [renderingType, setRenderingType] = useState(ScreenHelper.initRenderingType());
    const [landingRenderingType, setLandingRenderingType] = useState(ScreenHelper.initLandingRenderingType());

    useEffect(() => {
        ScreenHelper.getRenderingType(setRenderingType);
    }, []);

    useEffect(() => {
        ScreenHelper.getLandingRenderingType(setLandingRenderingType);
    }, []);

    const { i18n } = useTranslation();

    const mounted = useRef(true);
    const deferredRequests = useRef({});

    const asFailure = (error) => ({
        error: error?.code ?? "ERR_CMS_UNAVAILABLE",
        message: error?.message,
    });

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const loadDeferred = useCallback((key, request, setter) => {
        if (deferredRequests.current[key]) {
            return deferredRequests.current[key];
        }

        const pending = request()
            .then((data) => {
                if (mounted.current) {
                    setter(data);
                }
            })
            .catch((error) => {
                if (mounted.current) {
                    setter(asFailure(error));
                }
            });

        deferredRequests.current[key] = pending;
        return pending;
    }, []);

    const loadQuestionnaireData = useCallback(
        () => loadDeferred("questionnaires", () => CmsService.getQuestionnaires(i18n.language), setQuestionnaireData),
        [loadDeferred, i18n.language]
    );

    const loadProductBannersData = useCallback(
        () => loadDeferred("productBanners", () => CmsService.getProgramBanners(i18n.language), setProductBannersData),
        [loadDeferred, i18n.language]
    );

    useEffect(()=>{
        const controller = new AbortController();
        const options = {signal: controller.signal};
        let cancelled = false;

        const settle = (request) => request.then(
            (data) => ({resolved: true, data}),
            (error) => ({resolved: false, error})
        );

        const valueOf = (outcome) => outcome.resolved ? outcome.data : asFailure(outcome.error);

        const apply = (request, setter) => settle(request).then((outcome) => {
            if (!cancelled) {
                setter(valueOf(outcome));
            }
        });

        apply(CmsService.getLandingPageContent(i18n.language, options), setLandingData);
        apply(CmsService.getPickProgramPageContent(i18n.language, options), setPickProgramData);
        apply(CmsService.getLocales(options), setLocaleData);

        return () => {
            cancelled = true;
            controller.abort();
        };
    },[]) //eslint-disable-line react-hooks/exhaustive-deps

    return (
        <CmsContext.Provider value={{
            renderingType,
            landingRenderingType,
            landingData,
            pickProgramData,
            questionnaireData,
            productBannersData,
            localeData,
            loadQuestionnaireData,
            loadProductBannersData
        }}>
            {children}
        </CmsContext.Provider>
    )
}

export const useCms = () => useContext(CmsContext);